<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>{{ $t("org.billing.Project Cost") }}</h4>
            </div>
        </div>
        <b-card-body>
            <div class="d-flex justify-content-between flex-wrap">
                <!-- sorting  -->
                <b-form-group
                    class="mr-1 mb-md-0"
                >
                    <b-input-group size="sm">
                        <b-form-select id="sortBySelect" v-model="sortBy" :options="sortOptions">
                            <template #first>
                                <option value="">{{ $t('v4.none') }}</option>
                            </template>
                        </b-form-select>
                        <b-form-select v-model="sortDesc" size="sm" :disabled="!sortBy">
                            <option :value="false">{{ $t('v4.Asc') }}</option>
                            <option :value="true">{{ $t('v4.Desc') }}</option>
                        </b-form-select>
                    </b-input-group>
                </b-form-group>

                <!-- filter -->
                <b-form-group
                    class="mb-0"
                >
                    <b-input-group size="sm">
                        <b-form-input id="filterInput" v-model="filter" type="search" :placeholder="$t('v4.Enter account to Search')" />
                        <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">{{ $t('v4.Clear') }}</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </div>
        </b-card-body>
        <div class="b-table-sticky-header table-responsive">
            <b-table
                no-border-collapse
                :fields="fields"
                :items="items"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                :per-page="perPage"
                :current-page="currentPage"
                @filtered="onFiltered"
                show-empty
            >
                <template #cell(increaseCost)="item">
                    <i class="fa" :class="getIncreaseIconName(item.value)"></i>
                    {{ $_getDollar(item.value) }}
                </template>
                <template v-slot:empty>
                    <div class="d-flex justify-content-center align-items-center flex-column text-center">
                        <SVGEmptyIcon/>
                        <span>{{ $t('org.dashboard.No data') }}</span>
                    </div>
                </template>
            </b-table>
        </div>
        <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
            <!-- page length -->
            <b-form-group
                :label="$t('v4.Per Page')"
                label-cols="6"
                label-align="left"
                label-size="sm"
                label-for="sortBySelect"
                class="text-nowrap mb-md-0 mr-1"
            >
                <b-form-select id="perPageSelect" v-model="perPage" size="sm" inline :options="pageOptions" />
            </b-form-group>

            <!-- pagination -->
            <div>
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    first-number
                    last-number
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mb-0"
                >
                    <template v-slot:prev-text>
                        <feather-icon icon="ChevronLeftIcon" size="18" />
                    </template>
                    <template v-slot:next-text>
                        <feather-icon icon="ChevronRightIcon" size="18" />
                    </template>
                </b-pagination>
            </div>
        </b-card-body>
        <b-card-footer class="p-0">
            <b-button
                class="d-flex justify-content-between"
                block
                variant="primary"
                @click="downloadExcel"
            >
                <span>
                    <feather-icon icon="DownloadIcon" />
                    {{ $t('org.billing.Usage details XLSX of the consolidated account') }}
                </span>
                <span>
                    <small>
                        {{ formattedDate(lang, month, 'year-month') }}
                    </small>
                </span>
            </b-button>
        </b-card-footer>
    </b-card>
</template>
<script>
import { computed, toRefs, ref, getCurrentInstance, watch } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import SVGEmptyIcon from '@/assets/images/empty.svg'
import formattedDate from '@/utils/formattedDate'
export default {
    components: {
        SVGEmptyIcon
    },
    props: {
        finalData: {
            type: Object,
            default: function () {
                return {
                    finalEstimatedData: [],
                    finalPastData: []
                }
            }
        },
        month: {
            type: String,
            default: ''
        },
        prevMonth: {
            type: String,
            default: ''
        },
        estimated: {
            type: Boolean
        },
        status: {
            type: [String, Number],
            default: ''
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { __enMonthMap } = useCommonData()
        const { estimated, finalData, month, prevMonth } = toRefs(props)
        const finalEstimatedData = computed(() => finalData.value.finalEstimatedData)
        const finalPastData = computed(() => finalData.value.finalPastData)
        const items = computed(() => estimated.value ? finalEstimatedData.value : finalPastData.value)
        const filterOn = ref(['groupNamePath', 'projectName', 'usageAccountId'])
        const sumCost = computed(() => items.value.reduce((accumulator, currentValue) => (accumulator += currentValue.cost), 0))
        const lang = computed(() => vm.$i18n.locale)

        const labelOfPrevMonth = computed(() => estimated.value ? vm.$t('v4.Current Cost') : vm.$t('v4.Cost of', { month: vm.$t(`v4.${__enMonthMap[month.value.substring(5, 7)]}`) }))
        const labelOfNextMonth = computed(() => estimated.value ? vm.$t('v4.Estimated Cost') : vm.$t('v4.Cost of', { month: vm.$t(`v4.${__enMonthMap[prevMonth.value.substring(5, 7)]}`) }))

        const fields = computed(() => {
            return [
                {
                    label: vm.$t('v4.Group'),
                    tdClass: ['text-nowrap'],
                    key: 'groupNamePath',
                    sortable: true
                },
                {
                    label: vm.$t('v4.Project'),
                    key: 'projectName',
                    sortable: true
                },
                {
                    label: vm.$t('v4.AWS Account'),
                    key: 'usageAccountId',
                    sortable: true
                },
                {
                    label: vm.$t('v4.Ratio'),
                    key: 'ratio',
                    sortable: true,
                    thClass: ['text-right'],
                    tdClass: ['text-right text-nowrap'],
                    sortByFormatted: (value, key, item) => {
                        return (item.cost / sumCost.value) * 100
                    },
                    formatter: (value, key, item) => {
                        return vm.$_getRatio((item.cost / sumCost.value) * 100)
                    }
                },
                {
                    label: labelOfPrevMonth.value,
                    key: 'cost',
                    sortable: true,
                    thClass: ['text-right'],
                    tdClass: ['text-right', 'text-nowrap'],
                    formatter: (value, key, item) => {
                        return vm.$_getDollar(value)
                    }
                },
                {
                    label: labelOfNextMonth.value,
                    key: estimated.value ? 'estimatedCost' : 'prevMonthCost',
                    sortable: true,
                    thClass: ['text-right'],
                    tdClass: ['text-right text-nowrap'],
                    formatter: (value, key, item) => {
                        return vm.$_getDollar(value)
                    }
                },
                {
                    label: vm.$t('v4.Increase & Decrease'),
                    key: 'increaseCost',
                    thClass: ['text-right'],
                    tdClass: (value, key, item) => {
                        const increaseValue = estimated.value
                            ? item.estimatedCost - item.prevMonthCost
                            : item.cost - item.prevMonthCost
                        return ['text-right', 'text-nowrap', getIncreaseTextColor(increaseValue)]
                    },
                    sortable: true,
                    sortByFormatted: true,
                    formatter: (value, key, item) => {
                        return estimated.value
                            ? item.estimatedCost - item.prevMonthCost
                            : item.cost - item.prevMonthCost
                    }
                }
            ]
        })
        const sortOptions = computed(() => fields.value.filter(f => f.sortable).map(f => ({ text: f.label, value: f.key })))
        const filter = ref('')
        const sortBy = ref('')
        const sortDesc = ref(false)
        const currentPage = ref(1)
        const totalRows = ref(items.value.length)
        const perPage = ref(10)
        const pageOptions = [10, 20, 100]
        const onFiltered = (filteredItems) => {
            totalRows.value = filteredItems.length
            currentPage.value = 1
        }
        const getIncreaseIconName = (value) => {
            if (!estimated) return 'fa-exclamation-circle' // 'AlertCircleIcon'
            if (value > 0) {
                return 'fa-arrow-circle-up' // 'ArrowUpCircleIcon'
            } else if (value < 0) {
                return 'fa-arrow-circle-down' // 'ArrowDownCircleIcon'
            } else {
                return 'fa-minus-circle' // 'MinusCircleIcon'
            }
        }
        const getIncreaseTextColor = (value) => {
            if (!estimated) return
            if (value > 0) {
                return 'text-danger'
            } else if (value < 0) {
                return 'text-success'
            } else {
                return 'text-warnning'
            }
        }
        const downloadExcel = () => {
            context.emit('download:excel')
        }
        watch(items, (value, oldValue) => {
            totalRows.value = items.value.length
        })
        return {
            __enMonthMap,
            onFiltered,
            fields,
            filterOn,
            items,
            filter,
            sortOptions,
            sortBy,
            sortDesc,
            currentPage,
            totalRows,
            perPage,
            pageOptions,
            getIncreaseTextColor,
            getIncreaseIconName,
            downloadExcel,
            formattedDate,
            lang
        }
    }
}
</script>

<style>

</style>
